import React, { useEffect } from "react"
import { Skeleton, styled } from "@mui/material"

import { colors } from "theme"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectId } from "redux/features/auth/authSlice"
import { fetchClient } from "redux/features/client/clientThunk"
import {
  selectClientById,
  selectFetchStatus,
  selectUpdateStatus,
} from "redux/features/client/clientSlice"

import LoadableEntity from "components/ui/LoadableEntity"
import { ClientType } from "types"

export function withClient<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithClient = (props: GenericType) => {
    const dispatch = useAppDispatch()

    const clientId = useAppSelector((state) => selectId(state))
    const client = clientId
      ? useAppSelector(
          (state) => selectClientById(state, clientId) as ClientType,
        )
      : null

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))
    const updateStatus = useAppSelector((state) => selectUpdateStatus(state))

    const handleFetchClient = () => {
      if (!fetchStatus.isFetching) {
        dispatch(fetchClient())
      }
    }

    useEffect(() => handleFetchClient(), [])

    const renderSkeleton = () => {
      return (
        <Card sx={{ height: 770 }} animation="pulse" variant="rectangular" />
      )
    }

    return (
      <LoadableEntity
        entity={client}
        fetchStatus={fetchStatus}
        renderLoading={renderSkeleton}
      >
        {(client) => (
          <WrappedComponent
            {...props}
            client={client}
            fetchStatus={fetchStatus}
            updateStatus={updateStatus}
            fetchClient={handleFetchClient}
          />
        )}
      </LoadableEntity>
    )
  }

  return WithClient
}

const Card = styled(Skeleton)`
  background-color: ${colors.cultured};
  box-shadow: 12px 14px 23px -2px rgb(0 0 0 / 3%);
  border-radius: 26px;
  margin-bottom: 16px;
  padding: 58px;
`
